import { useRef, type ReactNode } from 'react'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { DURATION, EASE, gsap, useGSAP } from '@/lib/gsap'
import styles from './Magnetic.module.css'

/** Props for {@link Magnetic}. */
export interface MagneticProps {
  /** Pull strength: 0 = none, 1 = the child's centre follows the pointer. Default `0.35` (the max per DESIGN.md). */
  strength?: number
  className?: string
  /** Exactly one element. Its first element child is what moves. */
  children: ReactNode
}

/**
 * Pulls its child toward the pointer while hovered, and springs back on leave.
 * It is off for coarse pointers and reduced motion.
 *
 * @example <Magnetic><Button href={cta.button.href} external>{cta.button.label}</Button></Magnetic>
 */
export function Magnetic({ strength = 0.35, className, children }: MagneticProps) {
  const rootRef = useRef<HTMLSpanElement>(null)
  const reducedMotion = useReducedMotion()
  const coarsePointer = useMediaQuery(MEDIA.coarsePointer)
  const enabled = !reducedMotion && !coarsePointer

  useGSAP(
    () => {
      const root = rootRef.current
      const target = root?.firstElementChild
      if (!enabled || !root || !target) return

      const xTo = gsap.quickTo(target, 'x', { duration: DURATION.ui, ease: EASE.out })
      const yTo = gsap.quickTo(target, 'y', { duration: DURATION.ui, ease: EASE.out })

      const onMove = (event: PointerEvent) => {
        const rect = root.getBoundingClientRect()
        xTo((event.clientX - (rect.left + rect.width / 2)) * strength)
        yTo((event.clientY - (rect.top + rect.height / 2)) * strength)
      }
      const onLeave = () => {
        xTo(0)
        yTo(0)
      }

      root.addEventListener('pointermove', onMove)
      root.addEventListener('pointerleave', onLeave)
      return () => {
        root.removeEventListener('pointermove', onMove)
        root.removeEventListener('pointerleave', onLeave)
      }
    },
    { dependencies: [enabled, strength], revertOnUpdate: true },
  )

  return (
    <span ref={rootRef} className={cx(styles.magnetic, className)}>
      {children}
    </span>
  )
}
